/** Environmental / volumetric GLSL fragment shaders. */

export const FRAG_SUN_RAYS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_intensity;
uniform float u_spread;
uniform vec2 u_direction;
uniform vec3 u_color;
uniform float u_time;
out vec4 outColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = p * 2.07 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec4 base = texture(u_texture, v_uv);
  vec2 aspect = vec2(1.0, 1.0);
  vec2 lightDir = normalize(u_direction + vec2(0.0001));
  // Light source sits just off-frame so shafts cross the full screen.
  vec2 lightPos = vec2(0.5) - lightDir * 0.95;
  lightPos = clamp(lightPos, vec2(-0.15), vec2(1.15));

  // --- Volumetric god-ray march (radial light shafts) ---
  const int SAMPLES = 48;
  vec2 delta = (v_uv - lightPos) / float(SAMPLES);
  vec2 coord = v_uv;
  float decay = 0.965;
  float illumination = 0.0;
  float weight = 1.0;
  for (int i = 0; i < SAMPLES; i++) {
    coord -= delta;
    vec2 sampleUv = clamp(coord, 0.0, 1.0);
    float lum = dot(texture(u_texture, sampleUv).rgb, vec3(0.299, 0.587, 0.114));
    // Prefer bright regions + soft procedural dust so shafts read even on dark frames.
    float dust = fbm(sampleUv * 7.0 + lightDir * u_time * 0.15);
    float contrib = max(lum * 1.15, 0.18 + dust * 0.55);
    illumination += contrib * weight;
    weight *= decay;
  }
  illumination /= float(SAMPLES);

  // Soft angular shaft wedges for classic "sunlight through haze" look
  vec2 toPix = v_uv - lightPos;
  float dist = length(toPix * aspect);
  vec2 dir = normalize(toPix + vec2(0.0008));
  float align = max(0.0, dot(dir, lightDir));
  float wedge = pow(align, mix(14.0, 4.5, clamp(u_spread, 0.1, 1.5)));
  float shaftNoise = 0.55 + 0.45 * fbm(vec2(atan(toPix.y, toPix.x) * 3.0, dist * 2.5) + u_time * 0.08);
  float wedgeShaft = wedge * shaftNoise * exp(-dist * mix(1.6, 0.55, clamp(u_spread, 0.0, 1.5)));

  // Atmospheric bloom around light source (full-frame wash)
  float core = exp(-dist * dist * 3.5) * 1.1;
  float haze = exp(-dist * 0.85) * (0.35 + 0.25 * fbm(v_uv * 2.5 + u_time * 0.05));

  float raysAmt = (illumination * 1.55 + wedgeShaft * 1.25 + core * 0.9 + haze * 0.55)
    * u_intensity * 1.15;
  raysAmt = clamp(raysAmt, 0.0, 2.2);

  vec3 warm = u_color;
  vec3 cool = mix(u_color, vec3(1.0, 0.95, 0.85), 0.35);
  vec3 rays = mix(warm, cool, clamp(illumination, 0.0, 1.0)) * raysAmt;

  // Screen-style composite so midtones stay readable across the full frame
  vec3 outRgb = base.rgb + rays * (1.0 - base.rgb * 0.28);
  // Slight lift in shadowed areas under strong sunlight
  outRgb += warm * raysAmt * 0.04;
  outColor = vec4(outRgb, base.a);
}
`;

export const FRAG_FOG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
uniform float u_density;
uniform float u_height;
uniform float u_time;
out vec4 outColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p *= 2.05;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec4 base = texture(u_texture, v_uv);
  float h = pow(1.0 - v_uv.y, 1.0 / max(0.2, u_height));
  float n = fbm(v_uv * 3.2 + vec2(u_time * 0.35, u_time * 0.18));
  float detail = fbm(v_uv * 9.0 - vec2(u_time * 0.2, u_time * 0.1));
  float fog = clamp(u_density * h * (n * 0.7 + detail * 0.3) * 0.55, 0.0, 0.85);
  vec3 fogCol = mix(vec3(0.78, 0.82, 0.88), vec3(0.92, 0.94, 0.97), n);
  outColor = vec4(mix(base.rgb, fogCol, fog), base.a);
}
`;

export const FRAG_PARTICLE_OVERLAY = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_base;
uniform sampler2D u_particles;
uniform float u_intensity;
out vec4 outColor;
void main() {
  vec4 base = texture(u_base, v_uv);
  vec4 particles = texture(u_particles, v_uv);
  outColor = vec4(base.rgb + particles.rgb * u_intensity, base.a);
}
`;
